import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import {
  CheckIcon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const QuizBuilder = () => {
  // Question Approval System State
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [approvedQuestions, setApprovedQuestions] = useState([]);
  const [rejectedQuestions, setRejectedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Selection and UI state
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);

  // Modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approvingQuestionId, setApprovingQuestionId] = useState(null);
  const [rejectingQuestionId, setRejectingQuestionId] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  // Fetch questions on component mount and tab change
  useEffect(() => {
    fetchQuestions();
  }, [activeTab]);

  // Fetch questions based on active tab
  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);
    
    // Temporary mock data for testing while backend is being deployed
    const mockPendingQuestions = [
      {
        id: 1001,
        question: "What is the past tense of 'go'?",
        options: ["went", "goed", "gone", "going"],
        correct_answer: "went",
        status: "pending",
        submitted_by: "TestUser1",
        created_at: new Date().toISOString(),
        explanation: "The past tense of 'go' is 'went'."
      },
      {
        id: 1002,
        question: "Which is correct: 'I have been' or 'I has been'?",
        options: ["I have been", "I has been", "Both are correct", "Neither is correct"],
        correct_answer: "I have been",
        status: "pending",
        submitted_by: "TestUser2",
        created_at: new Date().toISOString(),
        explanation: "Use 'have' with 'I', not 'has'."
      }
    ];

    const mockApprovedQuestions = [
      {
        id: 1003,
        question: "What is the present perfect tense of 'eat'?",
        options: ["eaten", "ate", "have eaten", "eating"],
        correct_answer: "have eaten",
        status: "approved",
        submitted_by: "TestUser3",
        created_at: new Date().toISOString(),
        explanation: "Present perfect uses 'have/has + past participle'."
      }
    ];

    try {
      // Try production API first
      const endpoint =
        activeTab === "pending"
          ? "/question/pending"
          : activeTab === "approved"
          ? "/question/approved"
          : "/question/rejected";

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: localStorage.getItem("jstoken"),
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (activeTab === "pending") {
          setPendingQuestions(data.questions || []);
        } else if (activeTab === "approved") {
          setApprovedQuestions(data.questions || []);
        } else {
          setRejectedQuestions(data.questions || []);
        }
      } else {
        throw new Error("API not available");
      }
    } catch (err) {
      // Fallback to mock data for testing
      console.log("Using mock data - backend not deployed yet");
      if (activeTab === "pending") {
        setPendingQuestions(mockPendingQuestions);
      } else if (activeTab === "approved") {
        setApprovedQuestions(mockApprovedQuestions);
      } else {
        setRejectedQuestions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get current questions based on active tab
  const getCurrentQuestions = () => {
    switch (activeTab) {
      case "pending":
        return pendingQuestions;
      case "approved":
        return approvedQuestions;
      case "rejected":
        return rejectedQuestions;
      default:
        return [];
    }
  };

  // Approve a question
  const approveQuestion = async (questionId, notes = "") => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/question/approve/${questionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("jstoken"),
          },
          body: JSON.stringify({ admin_notes: notes }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to approve question");
      }

      setSuccess("Question approved successfully");
      setShowApproveModal(false);
      setApprovingQuestionId(null);
      setApprovalNotes("");

      // Refresh questions
      fetchQuestions();
    } catch (err) {
      setError(err.message);
    }
  };

  // Reject a question
  const rejectQuestion = async (questionId, reason) => {
    if (!reason.trim()) {
      setError("Rejection reason is required");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/question/reject/${questionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("jstoken"),
          },
          body: JSON.stringify({ rejection_reason: reason }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reject question");
      }

      setSuccess("Question rejected successfully");
      setShowRejectModal(false);
      setRejectingQuestionId(null);
      setRejectionReason("");

      // Refresh questions
      fetchQuestions();
    } catch (err) {
      setError(err.message);
    }
  };

  // Bulk approve questions
  const bulkApproveQuestions = async () => {
    if (selectedQuestions.length === 0) return;

    try {
      const promises = selectedQuestions.map((questionId) =>
        fetch(`${API_BASE_URL}/question/approve/${questionId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("jstoken"),
          },
          body: JSON.stringify({ admin_notes: "Bulk approved" }),
        })
      );

      await Promise.all(promises);
      setSuccess(`${selectedQuestions.length} questions approved successfully`);
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (err) {
      setError("Failed to bulk approve questions");
    }
  };

  // Bulk reject questions
  const bulkRejectQuestions = async () => {
    if (selectedQuestions.length === 0) return;

    try {
      const promises = selectedQuestions.map((questionId) =>
        fetch(`${API_BASE_URL}/question/reject/${questionId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("jstoken"),
          },
          body: JSON.stringify({ rejection_reason: "Bulk rejected" }),
        })
      );

      await Promise.all(promises);
      setSuccess(`${selectedQuestions.length} questions rejected successfully`);
      setSelectedQuestions([]);
      fetchQuestions();
    } catch (err) {
      setError("Failed to bulk reject questions");
    }
  };

  // Handle question selection
  const handleQuestionSelect = (questionId) => {
    setSelectedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Deselect all questions
  const deselectAllQuestions = () => {
    setSelectedQuestions([]);
  };

  // Toggle question expansion
  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestionId(
      expandedQuestionId === questionId ? null : questionId
    );
  };

  // Render tabs
  const renderTabs = () => (
    <div className="border-b border-gray-200 mb-6">
      <nav className="-mb-px flex space-x-8">
        {[
          { key: "pending", label: "Pending", count: pendingQuestions.length },
          {
            key: "approved",
            label: "Approved",
            count: approvedQuestions.length,
          },
          {
            key: "rejected",
            label: "Rejected",
            count: rejectedQuestions.length,
          },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === tab.key
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            {tab.label}
            <span
              className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${
                activeTab === tab.key
                  ? "bg-indigo-100 text-indigo-600"
                  : "bg-gray-100 text-gray-900"
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </nav>
    </div>
  );

  // Render bulk actions
  const renderBulkActions = () => {
    if (selectedQuestions.length === 0) return null;

    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-sm font-medium text-indigo-900">
              {selectedQuestions.length} question
              {selectedQuestions.length !== 1 ? "s" : ""} selected
            </span>
            <button
              onClick={deselectAllQuestions}
              className="ml-4 text-sm text-indigo-600 hover:text-indigo-800"
            >
              Clear selection
            </button>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={bulkApproveQuestions}
              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
            >
              Approve All
            </button>
            <button
              onClick={bulkRejectQuestions}
              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
            >
              Reject All
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render question card
  const renderQuestionCard = (question) => (
    <div
      key={question.id}
      className="bg-white shadow-md rounded-lg overflow-hidden mb-4"
    >
      <div className="px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1">
            {activeTab === "pending" && (
              <input
                type="checkbox"
                checked={selectedQuestions.includes(question.id)}
                onChange={() => handleQuestionSelect(question.id)}
                className="mt-1 mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
            )}
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <h3 className="text-lg font-medium text-gray-900">
                  {question.question}
                </h3>
                <span
                  className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    question.status === "pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : question.status === "approved"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {question.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
                <span className="flex items-center">
                  <UserIcon className="h-4 w-4 mr-1" />
                  {question.submitted_by || "Anonymous"}
                </span>
                <span className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  {new Date(question.created_at).toLocaleDateString()}
                </span>
                {question.course_name && (
                  <span className="flex items-center">
                    <span className="font-medium text-gray-900">Course:</span>
                    <span className="ml-1">{question.course_name}</span>
                  </span>
                )}
              </div>

              {question.explanation && (
                <p className="text-sm text-gray-600 mb-3">
                  {question.explanation}
                </p>
              )}

              {/* Show options in collapsed view */}
              <div className="grid grid-cols-2 gap-2">
                {question.options &&
                  question.options.map((option, index) => (
                    <div
                      key={index}
                      className={`text-xs px-2 py-1.5 rounded-md ${
                        option === question.correct_answer
                          ? "bg-green-100 border border-green-200 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {option}
                      {option === question.correct_answer && (
                        <CheckIcon className="inline-block h-3 w-3 ml-1" />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            {activeTab === "pending" && (
              <>
                <button
                  onClick={() => {
                    setApprovingQuestionId(question.id);
                    setShowApproveModal(true);
                  }}
                  className="p-1.5 rounded-full hover:bg-green-100"
                  title="Approve Question"
                >
                  <CheckIcon className="h-5 w-5 text-green-600" />
                </button>
                <button
                  onClick={() => {
                    setRejectingQuestionId(question.id);
                    setShowRejectModal(true);
                  }}
                  className="p-1.5 rounded-full hover:bg-red-100"
                  title="Reject Question"
                >
                  <XMarkIcon className="h-5 w-5 text-red-600" />
                </button>
              </>
            )}
            <button
              onClick={() => toggleQuestionExpansion(question.id)}
              className="p-1.5 rounded-full hover:bg-gray-100"
              title={
                expandedQuestionId === question.id
                  ? "Collapse Details"
                  : "Expand Details"
              }
            >
              {expandedQuestionId === question.id ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>
          </div>
        </div>

        {/* Expanded details */}
        {expandedQuestionId === question.id && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-900">
                  Question Type:
                </span>
                <span className="ml-2 text-gray-600">
                  {question.question_type || "Multiple Choice"}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-900">Difficulty:</span>
                <span className="ml-2 text-gray-600">
                  {question.difficulty_level === 1
                    ? "Easy"
                    : question.difficulty_level === 2
                    ? "Medium"
                    : "Hard"}
                </span>
              </div>
              {question.admin_notes && (
                <div className="md:col-span-2">
                  <span className="font-medium text-gray-900">
                    Admin Notes:
                  </span>
                  <p className="mt-1 text-gray-600">{question.admin_notes}</p>
                </div>
              )}
              {question.rejection_reason && (
                <div className="md:col-span-2">
                  <span className="font-medium text-red-900">
                    Rejection Reason:
                  </span>
                  <p className="mt-1 text-red-600">
                    {question.rejection_reason}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render questions list
  const renderQuestionsList = () => {
    const currentQuestions = getCurrentQuestions();

    if (loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    if (currentQuestions.length === 0) {
      return (
        <div className="bg-white shadow-md rounded-lg p-8 text-center">
          <QuestionMarkCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No {activeTab} questions found
          </h3>
          <p className="text-gray-500">
            {activeTab === "pending"
              ? "No questions are currently awaiting approval."
              : activeTab === "approved"
              ? "No questions have been approved yet."
              : "No questions have been rejected yet."}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {currentQuestions.map((question) => renderQuestionCard(question))}
      </div>
    );
  };

  // Render approval modal
  const renderApprovalModal = () => {
    if (!showApproveModal) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center mb-4">
              <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Approve Question
              </h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                rows="3"
                placeholder="Add any notes about this approval..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setApprovingQuestionId(null);
                  setApprovalNotes("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  approveQuestion(approvingQuestionId, approvalNotes)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                Approve Question
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render rejection modal
  const renderRejectionModal = () => {
    if (!showRejectModal) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Reject Question
              </h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows="3"
                placeholder="Please provide a reason for rejecting this question..."
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingQuestionId(null);
                  setRejectionReason("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  rejectQuestion(rejectingQuestionId, rejectionReason)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
              >
                Reject Question
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main component render
  return (
    <AdminLayout title="Question Approval System">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Question Approval System
          </h1>
          <p className="mt-2 text-gray-600">
            Review and manage user-submitted questions
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              {success}
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="text-green-700 hover:text-green-900"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Tabs */}
        {renderTabs()}

        {/* Bulk Actions */}
        {activeTab === "pending" && renderBulkActions()}

        {/* Questions List */}
        {renderQuestionsList()}

        {/* Modals */}
        {renderApprovalModal()}
        {renderRejectionModal()}
      </div>
    </AdminLayout>
  );
};

export default QuizBuilder;
